import { Stack, SxProps, Theme, Typography, TypographyProps } from '@mui/material'
import { useTranslate } from '@tolgee/react'
import { WarningIcon } from '@tom-ui/ui'
import { FC, ReactNode } from 'react'
import { RailcarPlanningDroppableRailcarProps } from '../Organisms/RailcarPlanningDroppableRailcar'

export type RailcarPlanningUnassignedContainerDroppableAreaProps = {
  isDepartedContainer: boolean
}

export const RailcarPlanningUnassignedContainerDroppableArea: FC<
  RailcarPlanningUnassignedContainerDroppableAreaProps
> = ({ isDepartedContainer }) => {
  const { t } = useTranslate()

  if (isDepartedContainer)
    return (
      <RailcarPlanningDisabledDroppableArea
        text={t('unassignmentLocked', 'Unassignment locked')}
        subtext={t('containerDeparted', 'Container departed')}
        sx={{ height: '145px' }}
      />
    )

  return <RailcarPlanningEnabledDroppableArea sx={{ height: '145px' }} />
}

export type RailcarPlanningDroppableAreaProps = Pick<
  RailcarPlanningDroppableRailcarProps,
  'railcar'
> & {
  isOver: boolean
  isSameRailcar: boolean
  isDragginContainer: boolean
}

export const RailcarPlanningDroppableArea: FC<RailcarPlanningDroppableAreaProps> = ({
  railcar,
  isOver,
  isSameRailcar,
  isDragginContainer,
}) => {
  const { t } = useTranslate()

  const showDroppableArea = isOver && !isSameRailcar && isDragginContainer && !railcar.isGap
  if (!showDroppableArea) return <></>

  if (railcar.isMissingRailcar)
    return (
      <RailcarPlanningDisabledDroppableArea
        text={t('assignmentLocked', 'Assignment locked')}
        subtext={t('missingRailcar', 'Missing railcar')}
        sx={{ width: '100%', minWidth: '160px' }}
      />
    )

  return <RailcarPlanningEnabledDroppableArea sx={{ width: '100%', minWidth: '160px' }} />
}

const RailcarPlanningEnabledDroppableArea: FC<{ sx?: SxProps<Theme> }> = ({ sx }) => {
  const { t } = useTranslate()

  return (
    <RailcarPlanningDroppableAreaBase
      text={t('dropContainerHere', 'Drop container here')}
      sx={sx}
    />
  )
}

type RailcarPlanningDisabledDroppableAreaProps = {
  text: string
  subtext?: string
  sx?: SxProps<Theme>
  textProps?: TypographyProps
}

const RailcarPlanningDisabledDroppableArea: FC<RailcarPlanningDisabledDroppableAreaProps> = ({
  text,
  sx,
  subtext,
  textProps,
}) => {
  return (
    <RailcarPlanningDroppableAreaBase
      text={text}
      sx={{ opacity: 0.5, ...sx }}
      icon={<WarningIcon color='warning' />}
      subtext={subtext}
      textProps={{ fontWeight: 'bold', ...textProps }}
    />
  )
}

type RailcarPlanningDroppableAreaBaseProps = {
  text: string
  icon?: ReactNode
  subtext?: string
  sx?: SxProps<Theme>
  textProps?: TypographyProps
}

const RailcarPlanningDroppableAreaBase: FC<RailcarPlanningDroppableAreaBaseProps> = ({
  text,
  icon,
  sx,
  subtext,
  textProps,
}) => {
  return (
    <Stack
      sx={{
        border: '2px dashed #ddd',
        borderRadius: '12px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        placeContent: 'center',
        padding: '8px',
        ...sx,
      }}
    >
      {icon}
      <Typography textAlign='center' variant='body2' {...textProps}>
        {text}
      </Typography>
      {!!subtext?.length && (
        <Typography textAlign='center' variant='caption'>
          {subtext}
        </Typography>
      )}
    </Stack>
  )
}
